import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, ArrowLeft, Crown, Sparkles, Building2, Zap, Tag, X, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_ICONS = {
  starter: Zap,
  pro: Sparkles,
  church: Building2,
  organization: Crown,
};

const TIER_COLORS = {
  starter: { bg: 'bg-[#F4F2EC]', border: 'border-[#E2DFD8]', accent: 'text-[#1B3A6B]' },
  pro: { bg: 'bg-[#1B3A6B]', border: 'border-[#1B3A6B]', accent: 'text-white' },
  church: { bg: 'bg-gradient-to-br from-[#1B3A6B] to-[#0F1A2A]', border: 'border-[#B8860B]', accent: 'text-white' },
  organization: { bg: 'bg-gradient-to-br from-[#B8860B] to-[#7C5800]', border: 'border-[#B8860B]', accent: 'text-white' },
};

export default function PricingPage({ isTrialSetup = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [currentTier, setCurrentTier] = useState('free');
  const [promoCode, setPromoCode] = useState('');
  const [promoValid, setPromoValid] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [customPlanForm, setCustomPlanForm] = useState(false);
  const [cpName, setCpName] = useState('');
  const [cpEmail, setCpEmail] = useState('');
  const [cpOrg, setCpOrg] = useState('');
  const [cpSize, setCpSize] = useState('');
  const [cpMessage, setCpMessage] = useState('');
  const [cpSubmitting, setCpSubmitting] = useState(false);
  const [cpSuccess, setCpSuccess] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment verification timed out. Please check your account.' });
      return;
    }
    try {
      const response = await axios.get(`${API}/subscriptions/status/${sid}`);
      const data = response.data;
      if (data.payment_status === 'paid') {
        setPaymentStatus({ status: 'success', message: `Upgraded to ${data.tier} plan!`, tier: data.tier });
        setCurrentTier(data.tier);
        return;
      }
      if (data.status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Payment session expired. Please try again.' });
        return;
      }
      setPaymentStatus({ status: 'processing', message: 'Payment is being processed...' });
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2500);
    } catch {
      setPaymentStatus({ status: 'error', message: 'Error checking payment status.' });
    }
  }, []);

  useEffect(() => {
    const loadTiers = async () => {
      try {
        const [tiersRes, currentRes] = await Promise.all([
          axios.get(`${API}/subscriptions/tiers`),
          axios.get(`${API}/subscriptions/current`)
        ]);
        setTiers(tiersRes.data);
        setCurrentTier(currentRes.data.tier);
      } catch (err) {
        console.error('Failed to load tiers:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTiers();
  }, []);

  useEffect(() => {
    if (sessionId) {
      const isTrial = searchParams.get('trial') === '1';
      if (isTrial) {
        // Trial checkout completed — go straight to the app
        setPaymentStatus({ status: 'success', message: 'Trial started! Redirecting...' });
        setTimeout(() => {
          window.location.href = '/app';
        }, 1500);
      } else {
        setPaymentStatus({ status: 'processing', message: 'Verifying payment...' });
        pollPaymentStatus(sessionId);
      }
    }
  }, [sessionId, searchParams, pollPaymentStatus]);

  const handleCheckout = async (tierKey) => {
    setCheckoutLoading(tierKey);
    try {
      const endpoint = isTrialSetup ? 'subscriptions/trial-checkout' : 'subscriptions/checkout';
      const response = await axios.post(`${API}/${endpoint}`, {
        tier: tierKey,
        origin_url: window.location.origin,
        promo_code: promoValid ? promoCode : null
      });
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert(err.response?.data?.detail || 'Checkout failed. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoError('');
    setPromoValid(null);
    try {
      const res = await axios.post(`${API}/promos/validate`, { code: promoCode });
      setPromoValid(res.data);
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid promo code');
      setPromoValid(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/subscriptions/portal`, { return_url: window.location.href });
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Portal error:', err);
      alert(err.response?.data?.detail || 'Unable to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1B3A6B] animate-spin" />
      </div>
    );
  }

  const tierOrder = ['starter', 'pro', 'church', 'organization'];

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="pricing-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2DFD8]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          {isTrialSetup ? (
            <span className="text-lg font-bold text-[#3D3229] tracking-wide uppercase" style={{fontFamily:"'Nunito', 'Quicksand', sans-serif", letterSpacing:'0.12em'}}>dsashun</span>
          ) : (
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#475569] hover:text-[#1B3A6B] transition-colors" data-testid="back-to-app">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to App</span>
            </button>
          )}
          {!isTrialSetup && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleManageBilling}
                disabled={portalLoading}
                className="flex items-center gap-2 px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] transition-colors disabled:opacity-50"
                data-testid="manage-billing-btn"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Manage Billing
              </button>
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#B8860B]" />
                <span className="text-sm font-medium text-[#475569]">
                  Current Plan: <span className="text-[#1B3A6B] font-bold capitalize">{currentTier}</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Payment Status Banner */}
      {paymentStatus && (
        <div className={`max-w-6xl mx-auto px-6 mt-6`}>
          <div className={`rounded-xl p-4 flex items-center gap-3 ${
            paymentStatus.status === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            paymentStatus.status === 'processing' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
            'bg-red-50 text-red-800 border border-red-200'
          }`} data-testid="payment-status-banner">
            {paymentStatus.status === 'processing' && <Loader2 className="w-5 h-5 animate-spin" />}
            {paymentStatus.status === 'success' && <Check className="w-5 h-5" />}
            <span className="font-medium">{paymentStatus.message}</span>
            {paymentStatus.status === 'success' && (
              <button onClick={() => navigate('/')} className="ml-auto text-sm font-bold underline" data-testid="go-to-app-btn">
                Go to App
              </button>
            )}
          </div>
        </div>
      )}

      {/* Pricing Hero */}
      <div className="max-w-6xl mx-auto px-6 py-16 text-center">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1B3A6B] tracking-tight mb-4" data-testid="pricing-title">
          {isTrialSetup ? 'Start Your 3-Day Free Trial' : 'Choose Your Plan'}
        </h1>
        <p className="text-[#475569] text-lg max-w-2xl mx-auto">
          {isTrialSetup 
            ? 'Choose a plan to begin your free trial. Your card won\'t be charged for 3 days — cancel anytime before then.'
            : 'Empower your ministry with AI-powered Bible study tools. Start free, upgrade when you\'re ready.'}
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tierOrder.map((tierKey) => {
            const tier = tiers[tierKey];
            if (!tier) return null;
            const isCurrentTier = currentTier === tierKey;
            const isPro = tierKey === 'pro';
            const colors = TIER_COLORS[tierKey];
            const Icon = TIER_ICONS[tierKey];

            return (
              <div
                key={tierKey}
                className={`relative rounded-2xl overflow-hidden transition-transform hover:scale-[1.02] ${
                  isPro ? 'ring-2 ring-[#B8860B] shadow-xl' : 'shadow-lg'
                }`}
                data-testid={`pricing-card-${tierKey}`}
              >
                {isPro && (
                  <div className="absolute top-0 left-0 right-0 bg-[#B8860B] text-white text-center text-xs font-bold py-1.5 uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <div className={`${colors.bg} p-8 ${isPro ? 'pt-12' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      tierKey === 'starter' ? 'bg-[#1B3A6B]/10' : 'bg-white/20'
                    }`}>
                      <Icon className={`w-5 h-5 ${tierKey === 'starter' ? 'text-[#1B3A6B]' : 'text-white'}`} strokeWidth={1.5} />
                    </div>
                    <h3 className={`text-xl font-bold ${colors.accent}`}>{tier.name}</h3>
                  </div>
                  <div className="mb-6">
                    <span className={`text-4xl font-bold ${colors.accent}`}>${tier.price.toFixed(2)}</span>
                    <span className={`text-sm ${tierKey === 'starter' ? 'text-[#475569]' : 'text-white/70'}`}>/month</span>
                  </div>
                </div>

                <div className="bg-white p-8">
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#0F1A2A]">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentTier ? (
                    <div className="w-full py-3 rounded-lg text-center font-medium text-sm bg-[#F4F2EC] text-[#475569]" data-testid={`current-plan-${tierKey}`}>
                      Current Plan
                    </div>
                  ) : (
                    <button
                      onClick={() => handleCheckout(tierKey)}
                      disabled={checkoutLoading === tierKey}
                      className={`w-full py-3 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                        isPro
                          ? 'bg-[#B8860B] text-white hover:bg-[#D19C11]'
                          : 'bg-[#1B3A6B] text-white hover:bg-[#254B8A]'
                      } disabled:opacity-50`}
                      data-testid={`checkout-btn-${tierKey}`}
                    >
                      {checkoutLoading === tierKey ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                      ) : isTrialSetup ? (
                        `Start Trial — ${tier.name}`
                      ) : (
                        `Upgrade to ${tier.name}`
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Promo Code */}
        <div className="max-w-md mx-auto mt-10 bg-white rounded-xl p-6 shadow-sm" data-testid="promo-section">
          <h3 className="text-sm font-semibold text-[#0F1A2A] mb-3 flex items-center gap-2">
            <Tag className="w-4 h-4 text-[#B8860B]" /> Have a Promo Code?
          </h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoValid(null); setPromoError(''); }}
              className="flex-1 px-4 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] font-mono text-sm"
              placeholder="Enter code..."
              data-testid="promo-input"
            />
            <button onClick={handleValidatePromo} className="px-4 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A]" data-testid="apply-promo-btn">
              Apply
            </button>
          </div>
          {promoValid && (
            <div className="mt-2 flex items-center gap-2 text-green-700 text-sm" data-testid="promo-success">
              <Check className="w-4 h-4" />
              {promoValid.discount_type === 'percentage' ? `${promoValid.discount_value}% off applied!` :
               promoValid.discount_type === 'fixed' ? `$${promoValid.discount_value} off applied!` :
               `${promoValid.discount_value} extra trial days!`}
            </div>
          )}
          {promoError && <p className="mt-2 text-red-600 text-sm">{promoError}</p>}
        </div>

        {/* Free tier note */}
        <div className="text-center mt-8 text-[#475569] text-sm">
          <p>Free tier includes 5 generations per month with basic Bible Study and Devotional access.</p>
          <p className="mt-1">All plans are billed monthly. Cancel anytime.</p>
        </div>

        {/* Custom Plan Request */}
        <div className="max-w-md mx-auto mt-10 bg-white rounded-xl p-6 shadow-sm border border-[#E2DFD8]" data-testid="custom-plan-section">
          <h3 className="text-sm font-semibold text-[#0F1A2A] mb-2">Need more than 100 seats?</h3>
          <p className="text-xs text-[#94A3B8] mb-4">We offer custom enterprise plans for large organizations, denominations, and ministry networks.</p>
          {cpSuccess ? (
            <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm flex items-center gap-2" data-testid="custom-plan-success">
              <Check className="w-4 h-4" /> Request submitted! We'll contact you within 24 hours.
            </div>
          ) : customPlanForm ? (
            <div className="space-y-2" data-testid="custom-plan-form">
              <div className="grid grid-cols-2 gap-2">
                <input value={cpName} onChange={e => setCpName(e.target.value)} placeholder="Your name" className="px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg" data-testid="cp-name" />
                <input value={cpEmail} onChange={e => setCpEmail(e.target.value)} placeholder="Email" type="email" className="px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg" data-testid="cp-email" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={cpOrg} onChange={e => setCpOrg(e.target.value)} placeholder="Organization name" className="px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg" data-testid="cp-org" />
                <input value={cpSize} onChange={e => setCpSize(e.target.value)} placeholder="Team size needed" className="px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg" data-testid="cp-size" />
              </div>
              <textarea value={cpMessage} onChange={e => setCpMessage(e.target.value)} placeholder="Tell us about your needs..." rows={2} className="w-full px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg resize-none" data-testid="cp-message" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setCustomPlanForm(false)} className="text-xs text-[#94A3B8]">Cancel</button>
                <button
                  onClick={async () => {
                    setCpSubmitting(true);
                    try {
                      await axios.post(`${API}/plans/custom-request`, { name: cpName, email: cpEmail, org_name: cpOrg, org_size: cpSize, message: cpMessage });
                      setCpSuccess(true);
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Failed to submit');
                    } finally {
                      setCpSubmitting(false);
                    }
                  }}
                  disabled={cpSubmitting || !cpName.trim() || !cpEmail.trim()}
                  className="px-4 py-2 bg-[#B8860B] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#D19C11]"
                  data-testid="cp-submit-btn"
                >
                  {cpSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setCustomPlanForm(true)}
              className="w-full py-2.5 border-2 border-dashed border-[#B8860B]/40 text-[#B8860B] rounded-lg text-sm font-medium hover:bg-amber-50 transition-colors"
              data-testid="custom-plan-toggle-btn"
            >
              Request Custom Enterprise Plan
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
